'use client';

import { type ReactNode, useEffect, useState } from 'react';

import type { User } from '@ps/functions/firebase/auth';
import { BehaviorSubject } from 'rxjs';

import { SessionWarmer } from './SessionWarmer';
import { AuthContext, type AuthContextValue } from './context';

type Props = {
  children?: ReactNode;
  /**
   * Set on a surface where a signed-in GUEST account is the acting identity, so the
   * warmer's app-start guest cleanup leaves it alone. Today only the standalone
   * `/checkout` tree, whose redirect-payment (3DS) return is a hard load — see
   * {@link warmSessionCookie}.
   */
  keepGuestSession?: boolean;
  /**
   * Set on the signup surfaces, where a signed-in user with no profile yet is
   * mid-signup rather than abandoned — see {@link warmSessionCookie}.
   */
  keepProfilelessSession?: boolean;
};

/**
 * Provides the app-wide {@link AuthContext} (`currentUserId$` / `currentUser$`).
 *
 * `firebase/auth` (~90KB) is **dynamic-imported inside the effect**, so it ships
 * as a post-hydration chunk rather than in a route's first-load bundle. This
 * matters most where the provider mounts on a public/visitor surface without
 * being lifted — the bio `@modal` / `@chat` parallel-route slots, whose
 * `layout.tsx` renders on EVERY bio page (even when the slot shows
 * `default.tsx`), so a static import put the auth SDK on the visitor's first
 * paint. Auth was already async — `onAuthStateChanged` never resolves at first
 * paint — so deferring the import changes nothing a consumer observes: they read
 * `undefined` while loading, then the resolved uid / user. Imports the
 * `@ps/functions/firebase/auth` submodule (not a barrel), so only `firebase/app`
 * + `firebase/auth` load, never firestore / storage.
 *
 * Renders {@link SessionWarmer}, which drives off this provider's `currentUser$`
 * to keep the server `__session` cookie reconciled with the client's auth state
 * (present before an intercepted modal opens). It's memoized at module scope, so
 * multiple mounted `AuthProvider`s don't re-hit the network.
 */
export function AuthProvider({
  children,
  keepGuestSession,
  keepProfilelessSession,
}: Props) {
  const [contextValue] = useState<AuthContextValue>(() => ({
    currentUserId$: new BehaviorSubject<string | null | undefined>(undefined),
    currentUser$: new BehaviorSubject<User | null | undefined>(undefined),
  }));

  const { currentUserId$, currentUser$ } = contextValue;

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let disposed = false;

    import('@ps/functions/firebase/auth')
      .then(({ auth, onAuthStateChanged }) => {
        if (disposed) return;
        const authInstance = auth();
        if (!authInstance) return;
        unsubscribe = onAuthStateChanged(authInstance, (user: User | null) => {
          currentUserId$.next(user?.uid ?? null);
          currentUser$.next(user ?? null);
        });
      })
      .catch((error: unknown) => {
        console.error('[AuthProvider] firebase auth load failed:', error);
      });

    return () => {
      disposed = true;
      unsubscribe?.();
    };
  }, [currentUserId$, currentUser$]);

  return (
    <AuthContext.Provider value={contextValue}>
      <SessionWarmer
        keepGuestSession={keepGuestSession}
        keepProfilelessSession={keepProfilelessSession}
      />
      {children}
    </AuthContext.Provider>
  );
}
